/**
 * The /ollama page: the ollama.com catalogue filtered by what fits in your VRAM,
 * with the local benchmark results overlaid on it.
 *
 * Built to server/static/ollama.min.js with `edwh build-js` (bun). Every piece of
 * markup comes from a <template> in ollama.html and is filled through
 * textContent, so nothing here ever concatenates HTML.
 */

/* ------------------------------------------------------------------ *
 * Payload types (see routes/ollama.py and lib/bench.py)
 * ------------------------------------------------------------------ */

interface Model {
    name: string;
    description: string;
    capabilities: string[];
    parameter_sizes: string[];
    pulls: string;
    tag_count: number;
    updated: string;
    url: string;
}

interface Variant {
    tag: string;
    bytes: number;
    gb: number;
}

interface Measurement {
    samples: number | null;
    correctness: number | null;
    duration_s: number | null;
}

interface Target {
    id: number;
    key: string;
    model: string;
    tag: string;
    source: string;
    status: string;
    memory_bytes: number | null;
    gpu_fraction: number | null;
    notes: string;
    runs: Record<string, Record<string, Measurement>>;
    averages: Record<string, number | null>;
    updated_at: string | null;
}

interface Entry {
    model: Model;
    rank: number;
    variants: Variant[] | null;
    error: boolean;
    off_catalogue?: boolean;
}

interface Config {
    // All three are served by Pretpakket: the catalogue from an hourly TTL cache,
    // the per-model tag sizes from a week-long on-disk cache, the bench rows from
    // its own database. Nothing here talks to ollama.com directly, and every
    // model's tag page is fetched at most once a week.
    catalogue_url: string;
    variants_url: string;
    bench_url: string;
    // Server-side cap on models per /variants call; keep in sync via the template.
    batch_size: number;
    vram_gb: number;
    headroom: number;
    logged_in: boolean;
    // filled from the meta block of /ollama/bench, so the vocabularies cannot
    // drift from lib/bench; the values in the JSON block are only the fallbacks.
    statuses: string[];
    sources: string[];
    run_kinds: string[];
    languages: string[];
    threshold: number;
}

interface BenchMeta {
    statuses: string[];
    sources: string[];
    run_kinds: string[];
    languages: string[];
    threshold: number;
}

interface Preferences {
    hidden?: string[];
}

/* ------------------------------------------------------------------ *
 * Configuration
 * ------------------------------------------------------------------ */

function read_config(): Config {
    const block = document.getElementById("ollama-config");
    if (!block) {
        throw new Error("No #ollama-config in the page.");
    }

    return JSON.parse(block.textContent || "{}") as Config;
}

const CONFIG = read_config();

// A quant within this fraction of the budget is flagged as a tight fit: it
// fits, but leaves little room for a long context window.
const TIGHT_AT = 0.9;
// The hidden models, and nothing else: a list of names is too long for a query
// string, and hiding one is meant to stick across visits. Everything else,
// budget included, lives in the URL (see the view state section below).
const STORAGE_KEY = "ollama-fit";
const NOT_MEASURED = "—";
// How many quant chips a card lists before collapsing the rest. The measured
// ones and the selected one are always among them, however long the tag list is.
const CHIP_LIMIT = 5;

/**
 * What actually became of a benchmarked quant.
 *
 * Also the order the "bench" sort walks, which is why it is an array: the
 * stored status only distinguishes kept from rejected from never-scored, and
 * "aborted" is read off the measurements themselves.
 */
const VERDICT_ORDER = ["kept", "rejected", "aborted", "unusable", "untested"] as const;

type VerdictKind = (typeof VERDICT_ORDER)[number];

interface Verdict {
    kind: VerdictKind;
    label: string;
    /** The best average of any run, or null when nothing was ever scored. */
    score: number | null;
}

/** The marker a quant chip carries for each verdict; empty means never measured. */
const CHIP_MARKS: Record<VerdictKind, string> = {
    kept: "✓",
    rejected: "✗",
    aborted: "·",
    unusable: "·",
    untested: "",
};

/* ------------------------------------------------------------------ *
 * State
 * ------------------------------------------------------------------ */

/** name -> entry */
const catalogue = new Map<string, Entry>();
/** "model:tag" -> bench target */
const bench_by_key = new Map<string, Target>();
/** model -> [bench target] */
const bench_by_model = new Map<string, Target[]>();

/** model -> the quant the reader picked, overriding the card's own choice */
const chosen_tags = new Map<string, string>();
/** models the reader does not want in this list at all */
const hidden_models = new Set<string>();

let sort_key = "rank";
let sort_desc = false;
let paused = false;
let resolving = false;
let current_sort = "newest";
let current_view = "cards";
let show_hidden = false;
// the capability select is filled from the catalogue, so a value from the URL
// has to wait for the options to exist before it can be applied
let wanted_capability = "";
let editing: { name: string; target: Target | null } | null = null;

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

function el<T extends HTMLElement = HTMLElement>(id: string): T {
    const node = document.getElementById(id);
    if (!node) {
        throw new Error(`No #${id} in the page.`);
    }

    return node as T;
}

function input(id: string): HTMLInputElement {
    return el<HTMLInputElement>(id);
}

function select(id: string): HTMLSelectElement {
    return el<HTMLSelectElement>(id);
}

/** Clone a <template>'s single root element, ready to be filled in. */
function clone<T extends Element = HTMLElement>(id: string): T {
    const template = el<HTMLTemplateElement>(id);
    const node = template.content.firstElementChild;
    if (!node) {
        throw new Error(`#${id} is empty.`);
    }

    return node.cloneNode(true) as T;
}

/** The one descendant matching `selector`; templates are ours, so it must exist. */
function part<T extends Element = HTMLElement>(root: ParentNode, selector: string): T {
    const node = root.querySelector<T>(selector);
    if (!node) {
        throw new Error(`Template is missing ${selector}.`);
    }

    return node;
}

function replace_children(host: Element, children: Node[]): void {
    host.replaceChildren(...children);
}

function pill(text: string): HTMLSpanElement {
    const span = document.createElement("span");
    span.className = "pill";
    span.textContent = text;
    return span;
}

function option(value: string, label: string, selected: boolean): HTMLOptionElement {
    const node = document.createElement("option");
    node.value = value;
    node.textContent = label;
    node.selected = selected;
    return node;
}

const $cards = el("cards");
const $cards_view = el<HTMLButtonElement>("cards-view");
const $table_container = el("table-container");
const $table_view = el<HTMLButtonElement>("table-view");
const $editor = el<HTMLDialogElement>("editor");

function budget_bytes(): number {
    return Number(input("vram").value) * Number(input("headroom").value) * 1e9;
}

/** "119.1M" -> 119100000, for sorting. */
function pulls_to_number(pulls: string): number {
    const match = /^([\d.]+)([KMB]?)$/.exec(pulls || "");
    if (!match) {
        return 0;
    }

    const scale: Record<string, number> = { K: 1e3, M: 1e6, B: 1e9 };
    return Number(match[1]) * (scale[match[2]] || 1);
}

function fitting_variants(entry: Entry): Variant[] | null {
    if (!entry.variants) {
        return null;
    }

    const budget = budget_bytes();
    // largest first: the biggest quant that fits is the best quality one.
    return entry.variants.filter((variant) => variant.bytes <= budget).sort((a, b) => b.bytes - a.bytes);
}

/** The bench rows of one catalogue entry. */
function bench_targets(entry: Entry): Target[] {
    return bench_by_model.get(entry.model.name) || [];
}

function bench_key(model: string, tag: string): string {
    return `${model}:${tag}`;
}

function target_for(entry: Entry, tag: string): Target | null {
    return bench_by_key.get(bench_key(entry.model.name, tag)) || null;
}

/** The best-scoring quant whose bench row passes `matches`, if any does. */
function best_matching_variant(
    entry: Entry,
    fits: Variant[],
    matches: (target: Target | null) => boolean,
): Variant | undefined {
    let best: Variant | undefined;
    // not -1: best_run_score itself returns -1 for a target that was never
    // scored, and an unusable quant still has to be able to win here
    let best_score = -Infinity;

    for (const variant of fits) {
        const target = target_for(entry, variant.tag);
        if (!matches(target)) {
            continue;
        }

        const score = best_run_score(target);
        // fits runs largest first, so a strict > keeps the bigger of an equal pair
        if (score > best_score) {
            best_score = score;
            best = variant;
        }
    }

    return best;
}

/**
 * The quant a card opens on: the one the bench has most to say about.
 *
 * Ranked by VERDICT_ORDER (the order the "bench" column sorts by), then by
 * score, then by size. A measured quant beats an unmeasured one even when the
 * measurement was a rejection: a labelled warning over an unlabelled gamble.
 */
function best_known_variant(entry: Entry, fits: Variant[]): Variant | undefined {
    let best: Variant | undefined;
    let best_rank: number = VERDICT_ORDER.length;
    let best_score = -Infinity;

    for (const variant of fits) {
        const verdict = verdict_of(target_for(entry, variant.tag));
        const rank = VERDICT_ORDER.indexOf(verdict.kind);
        const score = verdict.score ?? -Infinity;

        // fits runs largest first, so strict comparisons keep the bigger of a tie
        if (rank < best_rank || (rank === best_rank && score > best_score)) {
            best_rank = rank;
            best_score = score;
            best = variant;
        }
    }

    return best;
}

/**
 * The quant a card proposes when the reader has not picked one.
 *
 * Normally whatever is best known (above). The exception is a verdict filter:
 * while one is on, the card opens on the quant that matched it. Asking for the
 * unusable models and getting a card that says "kept" (because this model has
 * a better quant too) would be a broken promise, and the promise is the
 * filter's whole job.
 */
function default_variant(entry: Entry, fits: Variant[]): Variant | undefined {
    const coverage = select("coverage").value;
    if (VERDICT_ORDER.includes(coverage as VerdictKind)) {
        const matched = best_matching_variant(entry, fits, (target) => verdict_of(target).kind === coverage);
        if (matched) {
            return matched;
        }
    }

    return best_known_variant(entry, fits);
}

/** The quant a card is showing: the reader's pick if it still fits, else the default. */
function selected_variant(entry: Entry): Variant | undefined {
    const fits = fitting_variants(entry);
    if (!fits || !fits.length) {
        return undefined;
    }

    const chosen = chosen_tags.get(entry.model.name);
    return (chosen && fits.find((variant) => variant.tag === chosen)) || default_variant(entry, fits);
}

/**
 * The bench row a card speaks about: the one recorded for the selected quant.
 *
 * Nothing recorded for that quant means untested, and the chip markers say
 * which of the others were measured, so a verdict always has a subject you can
 * point at. A model with no sizeable quant at all (off-catalogue, or nothing
 * fits the budget) falls back to its best row, which is all there is to show.
 */
function selected_target(entry: Entry): Target | null {
    if (entry.off_catalogue) {
        return selected_bench_target(entry);
    }

    const selected = selected_variant(entry);
    return selected ? target_for(entry, selected.tag) : best_target(entry);
}

/**
 * The bench row an off-catalogue card is showing.
 *
 * Such a model has no published tags to size, so its chips are its bench rows
 * and the selection works on those directly: the reader's pick, else the row
 * the verdict filter asked for, else the best one. Same promise as
 * default_variant, without the sizes.
 */
function selected_bench_target(entry: Entry): Target | null {
    const targets = bench_targets(entry);
    const chosen = chosen_tags.get(entry.model.name);
    const picked = chosen ? targets.find((target) => target.tag === chosen) : undefined;
    if (picked) {
        return picked;
    }

    const coverage = select("coverage").value;
    if (VERDICT_ORDER.includes(coverage as VerdictKind)) {
        let matched: Target | null = null;
        for (const target of targets) {
            if (verdict_of(target).kind !== coverage) {
                continue;
            }
            if (!matched || best_run_score(target) > best_run_score(matched)) {
                matched = target;
            }
        }
        if (matched) {
            return matched;
        }
    }

    return best_target(entry);
}

/**
 * The most flattering row of a model.
 *
 * Sorting and the table's single line per model use this rather than the
 * selection, so neither moves when a card's chip is clicked.
 */
function best_target(entry: Entry): Target | null {
    let best: Target | null = null;
    for (const target of bench_targets(entry)) {
        if (!best || best_run_score(target) > best_run_score(best)) {
            best = target;
        }
    }

    return best;
}

/** How many languages this target has an actual score for. */
function measured_languages(target: Target): number {
    const languages = new Set<string>();
    for (const kind of CONFIG.run_kinds) {
        for (const [language, run] of Object.entries(target.runs?.[kind] || {})) {
            if (run.correctness !== null && run.correctness !== undefined) {
                languages.add(language);
            }
        }
    }

    return languages.size;
}

/**
 * What became of one benchmarked quant.
 *
 * The stored status says whether the result was kept, and "unusable" says the
 * model never produced a score at all: it crashed, or was so slow the run was
 * abandoned. Everything else is read off the measurements, because it is a fact
 * about them: a rejected target with only some languages scored was abandoned
 * halfway, not judged. The score travels with the verdict so a near miss can
 * read as a near miss without needing a category of its own.
 */
function verdict_of(target: Target | null): Verdict {
    if (!target) {
        return { kind: "untested", label: "untested", score: null };
    }

    const best = best_run_score(target);
    const score = best < 0 ? null : best;

    if (target.status === "benched") {
        return { kind: "kept", label: "kept", score };
    } else if (target.status === "unusable" || score === null) {
        return { kind: "unusable", label: "unusable", score };
    } else if (measured_languages(target) < CONFIG.languages.length) {
        return { kind: "aborted", label: "aborted", score };
    } else {
        return { kind: "rejected", label: "rejected", score };
    }
}

function load_hidden(): void {
    let stored: Preferences = {};
    try {
        stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") as Preferences;
    } catch {
        // private mode, or nothing stored yet
    }

    for (const name of stored.hidden || []) {
        hidden_models.add(name);
    }
}

function save_hidden(): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ hidden: [...hidden_models] } satisfies Preferences));
    } catch {
        // not worth bothering the user about
    }
}

/* ------------------------------------------------------------------ *
 * View state
 *
 * Every control belongs in the URL, the budget sliders included: state that
 * survives a reload without showing up anywhere is state you will forget you
 * set. A bare /ollama/ with a cleared query string is therefore always a clean
 * slate, falling back to the defaults the server sends, and any view you do set
 * can be linked to. Only the hidden models stay in localStorage.
 * ------------------------------------------------------------------ */

/** Control id, URL parameter, and the value that means "not set". */
const VIEW_CONTROLS: [string, string, string][] = [
    ["vram", "vram", String(CONFIG.vram_gb)],
    ["headroom", "budget", String(CONFIG.headroom)],
    ["sort", "sort", "newest"],
    ["coverage", "bench", "all"],
    ["order", "order", "rank"],
    ["capability", "can", ""],
    ["search", "q", ""],
];

function view_control(id: string): HTMLInputElement | HTMLSelectElement {
    return el<HTMLInputElement | HTMLSelectElement>(id);
}

function load_view_state(): void {
    const params = new URLSearchParams(window.location.search);

    for (const [id, key, fallback] of VIEW_CONTROLS) {
        const value = params.get(key) ?? fallback;
        // the capability options do not exist until the catalogue lands
        if (id === "capability") {
            wanted_capability = value;
            continue;
        }
        view_control(id).value = value;
    }

    input("only-fitting").checked = params.get("fit") !== "all";
    show_hidden = params.get("hidden") === "show";
    current_view = params.get("view") === "table" ? "table" : "cards";
    apply_order(select("order").value || "rank");
}

function save_view_state(): void {
    const params = new URLSearchParams();

    for (const [id, key, fallback] of VIEW_CONTROLS) {
        const value = view_control(id).value.trim();
        if (value && value !== fallback) {
            params.set(key, value);
        }
    }

    if (!input("only-fitting").checked) {
        params.set("fit", "all");
    }
    if (show_hidden) {
        params.set("hidden", "show");
    }
    if (current_view !== "cards") {
        params.set("view", current_view);
    }

    // replaceState rather than pushState: the back button should leave the page,
    // not walk back through every keystroke in the search box
    const query = params.toString();
    window.history.replaceState(null, "", query ? `?${query}` : window.location.pathname);
}

/**
 * Point the sort at one key, descending for anything numeric.
 *
 * The table headers and the "order by" select share this, so the card view can
 * be sorted too and the two never disagree.
 */
function apply_order(key: string): void {
    sort_key = key;
    sort_desc = key !== "rank";
}

/* ------------------------------------------------------------------ *
 * Filtering and sorting
 * ------------------------------------------------------------------ */

function matches_coverage(entry: Entry): boolean {
    const coverage = select("coverage").value;
    if (coverage === "all") {
        return true;
    }

    const targets = bench_targets(entry);

    if (coverage === "missing") {
        // the to-bench queue: in the catalogue, fits the budget, never measured.
        if (targets.length || entry.off_catalogue) {
            return false;
        }

        const fits = fitting_variants(entry);
        return fits === null || fits.length > 0;
    }

    return targets.some((target) => verdict_of(target).kind === coverage);
}

function matches_search(entry: Entry, needle: string): boolean {
    if (!needle) {
        return true;
    }

    return entry.model.name.toLowerCase().includes(needle) || entry.model.description.toLowerCase().includes(needle);
}

function visible_entries(): Entry[] {
    const needle = input("search").value.trim().toLowerCase();
    const capability = select("capability").value;
    const only_fitting = input("only-fitting").checked;

    return [...catalogue.values()].filter((entry) => {
        if (hidden_models.has(entry.model.name) && !show_hidden) {
            return false;
        }
        if (!matches_search(entry, needle)) {
            return false;
        }
        if (capability && !entry.model.capabilities.includes(capability)) {
            return false;
        }
        if (!matches_coverage(entry)) {
            return false;
        }
        if (!only_fitting) {
            return true;
        }
        // a benchmarked model the catalogue cannot see has no size to judge, and
        // hiding it would hide your own results
        if (entry.off_catalogue) {
            return true;
        }

        const fits = fitting_variants(entry);
        // unresolved models stay visible so you can see the sweep filling them in
        return fits === null || fits.length > 0;
    });
}

function best_run_score(target: Target | null): number {
    // the best run of either size: a full run is more trustworthy, but a model
    // that only has a triage run should still sort on what it has
    const scores = CONFIG.run_kinds
        .map((kind) => target?.averages?.[kind])
        .filter((score): score is number => score !== null && score !== undefined);

    return scores.length ? Math.max(...scores) : -1;
}

function sort_value(entry: Entry, key: string): number {
    const fits = fitting_variants(entry);
    const target = best_target(entry);

    switch (key) {
        case "best":
            return fits && fits.length ? fits[0].bytes : -1;
        case "smallest":
            return fits && fits.length ? fits[fits.length - 1].bytes : -1;
        case "options":
            return fits ? fits.length : -1;
        case "pulls":
            return pulls_to_number(entry.model.pulls);
        case "score":
            return best_run_score(target);
        case "bench":
            return VERDICT_ORDER.indexOf(verdict_of(target).kind);
        default:
            return entry.rank;
    }
}

/* ------------------------------------------------------------------ *
 * Rendering
 * ------------------------------------------------------------------ */

function fill_size_cell(cell: HTMLTableCellElement, variant: Variant | null, budget: number): void {
    if (!variant) {
        cell.classList.add("miss");
        cell.textContent = NOT_MEASURED;
        return;
    }

    cell.classList.add(variant.bytes > budget * TIGHT_AT ? "tight" : "fit");
    cell.title = `${variant.tag}: ${variant.bytes.toLocaleString()} bytes`;
    cell.textContent = `${variant.gb}GB `;
    cell.append(pill(variant.tag));
}

function fill_score_cell(cell: HTMLTableCellElement, target: Target | null, run_kind: string): void {
    const average = target?.averages?.[run_kind];
    if (average === null || average === undefined || !target) {
        cell.classList.add("pending");
        cell.textContent = NOT_MEASURED;
        return;
    }

    cell.classList.add(average >= CONFIG.threshold ? "fit" : "miss");
    cell.title = Object.entries(target.runs[run_kind] || {})
        .map(([language, run]) => `${language} ${run.correctness ?? "?"}`)
        .join(", ");
    cell.textContent = average.toFixed(2);
}

/**
 * The verdict pill: what happened, and the number it happened on.
 *
 * Paper ground with a coloured dot rather than a coloured fill, because the
 * cards rotate through four background colours and a green pill on a green card
 * is not a pill.
 */
function verdict_node(verdict: Verdict): DocumentFragment {
    const fragment = document.createDocumentFragment();

    const badge = document.createElement("span");
    badge.className = `bench-pill bench-${verdict.kind}`;

    const dot = document.createElement("span");
    dot.className = "bench-dot";
    dot.setAttribute("aria-hidden", "true");
    badge.append(dot, verdict.label);
    fragment.append(badge);

    if (verdict.score !== null) {
        const gap = document.createElement("span");
        gap.className = "bench-gap";
        // the bar alongside the score: 0.67 / 0.70 is a near miss, 0.03 / 0.70
        // is a different animal, and neither needs a word for it
        gap.textContent = `${verdict.score.toFixed(2)} / ${CONFIG.threshold.toFixed(2)}`;
        gap.title = "best run average against the triage threshold";
        fragment.append(gap);
    }

    return fragment;
}

/** The dismiss control in a card's top corner; pressed means currently hidden. */
function hide_button(name: string): HTMLButtonElement {
    const hidden = hidden_models.has(name);
    const button = clone<HTMLButtonElement>("hide-button-template");

    button.dataset.hide = name;
    button.setAttribute("aria-pressed", String(hidden));
    button.title = hidden ? `Show ${name} again` : `Hide ${name} from this list`;
    button.setAttribute("aria-label", button.title);
    button.textContent = hidden ? "↩" : "✕";
    return button;
}

/**
 * The edit control. `tag` is where a fresh recording lands when nothing is
 * stored yet, so pressing "record" on a card opens on the quant you are looking
 * at rather than on whatever the tag list happens to start with.
 */
function edit_button(entry: Entry, target: Target | null, tag: string): HTMLButtonElement | null {
    if (!CONFIG.logged_in) {
        return null;
    }

    const button = clone<HTMLButtonElement>("edit-button-template");
    button.textContent = target ? "edit" : "record";
    button.dataset.edit = entry.model.name;
    button.dataset.tag = target?.tag || tag;
    return button;
}

function quant_note(text: string): HTMLSpanElement {
    const note = document.createElement("span");
    note.className = "quant-note";
    note.textContent = text;
    return note;
}

/**
 * One quant, as a radio in the card's chip strip.
 *
 * Two independent things are encoded here on purpose, because they used to be
 * conflated: the *selection* (filled, and it really is a control now) and the
 * *verdict* on that specific quant (the leading marker). So you can see at a
 * glance which of the quants your card can hold were ever put through the bench.
 */
function quant_chip(entry: Entry, variant: Variant, selected: boolean, budget: number): HTMLButtonElement {
    const $chip = tag_chip(entry, variant.tag, selected);

    if (variant.bytes > budget * TIGHT_AT) {
        $chip.classList.add("is-tight");
        $chip.title = "fits, but leaves little room for a context window";
    }

    $chip.append(` · ${variant.gb.toFixed(1)}G`);
    return $chip;
}

/** The radio and verdict marker every chip shares, labelled with the bare tag. */
function tag_chip(entry: Entry, tag: string, selected: boolean): HTMLButtonElement {
    const $chip = document.createElement("button");
    $chip.type = "button";
    $chip.setAttribute("role", "radio");
    $chip.setAttribute("aria-checked", String(selected));
    // one tab stop for the whole group, then arrow keys inside it
    $chip.tabIndex = selected ? 0 : -1;
    $chip.dataset.pick = entry.model.name;
    $chip.dataset.tag = tag;

    const verdict = verdict_of(target_for(entry, tag));
    const mark = CHIP_MARKS[verdict.kind];
    if (mark) {
        const $glyph = document.createElement("span");
        $glyph.className = `quant-mark mark-${verdict.kind}`;
        $glyph.textContent = mark;
        $glyph.title = verdict.label;
        $chip.append($glyph);
    }

    $chip.append(tag);
    return $chip;
}

/**
 * Which quants a card lists.
 *
 * The measured ones and the selected one always survive the cut, however far
 * down the size order they sit: a chip strip that hides the quant the card is
 * proposing would be worse than no strip at all.
 */
function shown_variants(entry: Entry, fits: Variant[], selected: Variant | undefined): Variant[] {
    const keep = new Set<Variant>();

    if (selected) {
        keep.add(selected);
    }
    for (const variant of fits) {
        if (target_for(entry, variant.tag)) {
            keep.add(variant);
        }
    }
    for (const variant of fits) {
        if (keep.size >= CHIP_LIMIT) {
            break;
        }
        keep.add(variant);
    }

    return fits.filter((variant) => keep.has(variant));
}

/** The chip strip of a card: what fits, why nothing fits, or that we don't know yet. */
function quant_nodes(entry: Entry, fits: Variant[] | null, selected: Variant | undefined): Node[] {
    if (entry.off_catalogue) {
        // no published tags to size, so the recorded ones are the chips; each
        // was written as a row of its own and would otherwise have no way in
        const shown = selected_bench_target(entry);
        return [
            ...bench_targets(entry).map((target) => tag_chip(entry, target.tag, target === shown)),
            quant_note("not in the library"),
        ];
    }
    if (!fits) {
        return [quant_note(entry.error ? "tag page unreadable" : "checking published tags…")];
    }
    if (!fits.length) {
        return [quant_note("no quant fits this budget")];
    }

    const budget = budget_bytes();
    const shown = shown_variants(entry, fits, selected);
    const nodes: Node[] = shown.map((variant) => quant_chip(entry, variant, variant === selected, budget));

    if (shown.length < fits.length) {
        nodes.push(quant_note(`+${fits.length - shown.length}`));
    }

    return nodes;
}

/**
 * The line above the pull command: predicted size against measured size.
 *
 * The predicted number is the quant's file size, the measured one is what the
 * model actually took on the GPU, and they are routinely a gigabyte or two
 * apart. Neither appeared anywhere on this page before, while the tag itself
 * appeared three times.
 */
function metrics_line(entry: Entry, selected: Variant | undefined, target: Target | null): string {
    const parts: string[] = [];

    if (selected) {
        parts.push(`predicted ${selected.gb.toFixed(1)} GB`);
    }
    if (target?.memory_bytes) {
        parts.push(`measured ${(target.memory_bytes / 1e9).toFixed(1)} GB`);
    }
    if (target?.gpu_fraction !== null && target?.gpu_fraction !== undefined) {
        parts.push(`${Math.round(target.gpu_fraction * 100)}% on GPU`);
    }

    if (parts.length) {
        return parts.join(" · ");
    }

    return entry.model.tag_count ? `${entry.model.tag_count} published tags` : "";
}

function card_node(entry: Entry): HTMLElement {
    const model = entry.model;
    const fits = fitting_variants(entry);
    const selected = selected_variant(entry);
    const target = selected_target(entry);
    const card = clone("catalogue-card-template");

    if (hidden_models.has(model.name)) {
        card.classList.add("is-hidden-model");
    }

    part(card, ".catalogue-orb").textContent = model.name[0].toUpperCase();
    replace_children(part(card, ".catalogue-dismiss"), [hide_button(model.name)]);

    const link = part<HTMLAnchorElement>(card, ".catalogue-title a");
    link.href = model.url;
    link.textContent = model.name;

    part(card, ".catalogue-description").textContent = model.description;
    replace_children(part(card, ".bench-slot"), [verdict_node(verdict_of(target))]);

    const $quants = part(card, ".catalogue-tags");
    $quants.setAttribute("role", "radiogroup");
    $quants.setAttribute("aria-label", `Quantisation of ${model.name}`);
    replace_children($quants, quant_nodes(entry, fits, selected));

    part(card, ".catalogue-metrics").textContent = metrics_line(entry, selected, target);

    const $notes = part(card, ".catalogue-notes");
    $notes.textContent = target?.notes || "";
    $notes.hidden = !target?.notes;

    const $command = part(card, ".catalogue-command");
    $command.hidden = !selected;
    if (selected) {
        $command.textContent = `ollama pull ${model.name}:${selected.tag}`;
    }

    const button = edit_button(entry, target, selected?.tag || "");
    if (button) {
        part(card, ".catalogue-actions").append(button);
    }

    return card;
}

/** The three size columns, collapsed into one note when there is nothing to size. */
function fill_size_columns(row: HTMLElement, entry: Entry, fits: Variant[] | null): void {
    const best_cell = part<HTMLTableCellElement>(row, ".best-cell");
    const smallest_cell = part<HTMLTableCellElement>(row, ".smallest-cell");
    const options_cell = part<HTMLTableCellElement>(row, ".options-cell");

    let note = "";
    if (entry.off_catalogue) {
        note = "not in the library";
    } else if (entry.error) {
        note = "tag page unreadable";
    } else if (!fits) {
        note = `${entry.model.tag_count} tags, not checked yet`;
    }

    if (note) {
        best_cell.colSpan = 3;
        best_cell.classList.add(entry.error ? "miss" : "pending");
        best_cell.textContent = note;
        smallest_cell.remove();
        options_cell.remove();
        return;
    }

    const budget = budget_bytes();
    const variants = fits as Variant[];
    fill_size_cell(best_cell, variants[0] || null, budget);
    fill_size_cell(smallest_cell, variants.length > 1 ? variants[variants.length - 1] : null, budget);
    if (!variants.length) {
        options_cell.classList.add("miss");
    }
    options_cell.textContent = `${variants.length} / ${entry.variants?.length ?? 0}`;
}

function row_node(entry: Entry): HTMLElement {
    const model = entry.model;
    const fits = fitting_variants(entry);
    // the table keeps one line per model, so it reads the best row rather than
    // the per-card selection
    const target = best_target(entry);
    const row = clone("catalogue-row-template");

    const name_cell = part(row, ".name");
    if (entry.off_catalogue) {
        name_cell.classList.add("off-catalogue");
    }
    if (hidden_models.has(model.name)) {
        name_cell.classList.add("is-hidden-model");
    }
    name_cell.append(hide_button(model.name));

    const link = part<HTMLAnchorElement>(row, ".name a");
    link.href = model.url;
    link.textContent = model.name;

    const description = part(row, ".desc");
    description.title = model.description;
    description.textContent = model.description;

    const command = part(row, ".pull-command");
    const has_fit = Boolean(fits && fits.length);
    command.hidden = !has_fit;
    if (fits && fits.length) {
        command.textContent = `ollama pull ${model.name}:${fits[0].tag}`;
    }

    fill_size_columns(row, entry, fits);

    const bench_cell = part(row, ".bench-cell");
    bench_cell.append(verdict_node(verdict_of(target)));
    if (target?.tag) {
        bench_cell.append(" ", pill(target.tag));
    }
    if (target?.notes) {
        const note = document.createElement("span");
        note.className = "bench-note";
        note.title = target.notes;
        note.textContent = target.notes;
        bench_cell.append(note);
    }
    const button = edit_button(entry, target, fits?.[0]?.tag || "");
    if (button) {
        bench_cell.append(button);
    }

    fill_score_cell(part<HTMLTableCellElement>(row, ".triage-cell"), target, "triage");
    fill_score_cell(part<HTMLTableCellElement>(row, ".full-cell"), target, "full");

    replace_children(part(row, ".capabilities-cell"), [...model.capabilities, ...model.parameter_sizes].map(pill));
    part(row, ".pulls-cell").textContent = model.pulls;
    part(row, ".updated-cell").textContent = model.updated;

    return row;
}

function set_view(next_view: string): void {
    current_view = next_view;
    save_view_state();
    render();
}

function render_meta(): void {
    const all = [...catalogue.values()];
    const resolved = all.filter((entry) => entry.variants || entry.error).length;
    const fitting = all.filter((entry) => (fitting_variants(entry) || []).length).length;
    const missing = all.filter(
        (entry) => !entry.off_catalogue && !bench_targets(entry).length && (fitting_variants(entry) || []).length,
    ).length;
    const budget_gb = (budget_bytes() / 1e9).toFixed(1);

    const $hidden = el<HTMLButtonElement>("hidden-toggle");
    $hidden.hidden = hidden_models.size === 0;
    $hidden.textContent = `${hidden_models.size} hidden`;
    $hidden.setAttribute("aria-pressed", String(show_hidden));
    $hidden.title = show_hidden ? "Hide them again" : "Show the models you hid";

    el("budget-out").textContent = `${budget_gb}GB for weights`;
    el("meta").textContent =
        `${catalogue.size} models · ${resolved} checked` +
        ` · ${fitting} fit in ${budget_gb}GB` +
        ` · ${bench_by_key.size} benchmarked · ${missing} fitting and unbenched`;
    el("bar").style.width = catalogue.size ? `${(resolved / catalogue.size) * 100}%` : "0";
}

function render(): void {
    const entries = visible_entries();

    // rows without sizes yet (or with an unreadable tag page) carry no sortable
    // number, so they trail the sorted ones in catalogue order either way
    const known = entries.filter((entry) => entry.variants);
    const unknown = entries.filter((entry) => !entry.variants).sort((a, b) => a.rank - b.rank);

    known.sort((a, b) => {
        const delta = sort_value(a, sort_key) - sort_value(b, sort_key);
        return sort_desc ? -delta : delta;
    });

    const sorted_entries = [...known, ...unknown];
    const cards_visible = current_view === "cards";

    $cards_view.setAttribute("aria-pressed", String(cards_visible));
    $table_view.setAttribute("aria-pressed", String(!cards_visible));
    $cards.hidden = !cards_visible || entries.length === 0;
    $table_container.hidden = cards_visible || entries.length === 0;
    if (cards_visible) {
        replace_children($cards, sorted_entries.map(card_node));
    } else {
        replace_children(el("rows"), sorted_entries.map(row_node));
    }

    el("status").hidden = entries.length > 0;
    if (!entries.length) {
        el("status").textContent = "Nothing matches those filters.";
    }

    render_meta();
}

function render_session(): void {
    const template = CONFIG.logged_in ? "logout-template" : "login-template";
    replace_children(el("session"), [clone(template)]);
    el("record-model").hidden = !CONFIG.logged_in;
}

/* ------------------------------------------------------------------ *
 * Loading
 * ------------------------------------------------------------------ */

async function load_catalogue(sort: string): Promise<void> {
    const res = await fetch(`${CONFIG.catalogue_url}?sort=${encodeURIComponent(sort)}`);
    if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
    }

    const payload = (await res.json()) as { models: Model[] };

    catalogue.clear();
    payload.models.forEach((model, index) => {
        catalogue.set(model.name, { model, rank: index, variants: null, error: false });
    });

    const capabilities = [...new Set(payload.models.flatMap((model) => model.capabilities))].sort();
    replace_children(select("capability"), [
        option("", "anything", true),
        ...capabilities.map((capability) => option(capability, capability, false)),
    ]);
    // the options only exist now, so a "can" from the URL is applied here
    select("capability").value = wanted_capability;
}

function index_bench(targets: Target[]): void {
    bench_by_key.clear();
    bench_by_model.clear();

    // an off-catalogue entry exists only because a bench row did, so a row
    // deleted in this session must not leave its card standing; add_off_catalogue
    // puts back the ones that are still there. deleting while iterating a Map is
    // defined behaviour.
    for (const [name, entry] of catalogue) {
        if (entry.off_catalogue) {
            catalogue.delete(name);
        }
    }

    for (const target of targets) {
        bench_by_key.set(target.key, target);
        const existing = bench_by_model.get(target.model);
        if (existing) {
            existing.push(target);
        } else {
            bench_by_model.set(target.model, [target]);
        }
    }

    add_off_catalogue();
}

/**
 * Give benchmarked models the catalogue cannot see a row of their own.
 *
 * A namespaced or hand-built GGUF model never appears in /library, so without
 * this the work done on it would be invisible here.
 */
function add_off_catalogue(): void {
    for (const [name, targets] of bench_by_model) {
        if (catalogue.has(name)) {
            continue;
        }

        const target = targets[0];
        catalogue.set(name, {
            model: {
                name,
                description: `${target.source} model, benchmarked locally`,
                capabilities: [],
                parameter_sizes: [],
                pulls: "",
                tag_count: 0,
                updated: "",
                url: `https://ollama.com/search?q=${encodeURIComponent(name)}`,
            },
            // after everything the catalogue itself lists:
            rank: 100000 + catalogue.size,
            variants: [],
            error: false,
            off_catalogue: true,
        });
    }
}

async function load_bench(): Promise<void> {
    const res = await fetch(CONFIG.bench_url);
    if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
    }

    const payload = (await res.json()) as { meta: BenchMeta; targets: Target[] };
    Object.assign(CONFIG, payload.meta);

    index_bench(payload.targets);
}

/** One /variants batch; false means the sweep should stop. */
async function resolve_batch(todo: Entry[]): Promise<boolean> {
    const query = todo.map((entry) => `models=${encodeURIComponent(entry.model.name)}`).join("&");

    let payload: Record<string, { error: boolean; variants: Variant[] }>;
    try {
        const res = await fetch(`${CONFIG.variants_url}?${query}`);
        if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
        }
        payload = await res.json();
    } catch (err) {
        // network hiccup: stop sweeping rather than hammering, keep what we have
        el("status").hidden = false;
        el("status").textContent = `Stopped checking tags: ${message_of(err)}. Press Resume to retry.`;
        paused = true;
        el("pause").textContent = "Resume";
        return false;
    }

    let progressed = 0;
    for (const [name, result] of Object.entries(payload)) {
        const entry = catalogue.get(name);
        if (!entry) {
            continue;
        }
        entry.error = result.error;
        entry.variants = result.error ? null : result.variants;
        progressed++;
    }

    render();

    // a batch that resolved nothing would loop forever asking for the same models
    return progressed > 0;
}

/**
 * Walk the catalogue in batches, filling in tag sizes as they come back.
 *
 * One batch at a time on purpose: the server caps a batch at CONFIG.batch_size
 * models and fetches at most a few tag pages in parallel, so a page view can
 * never turn into a burst of hundreds of requests to ollama.com.
 */
async function resolve_all(): Promise<void> {
    if (resolving) {
        return;
    }
    resolving = true;

    try {
        while (!paused) {
            const todo = [...catalogue.values()]
                .filter((entry) => !entry.variants && !entry.error && !entry.off_catalogue)
                .sort((a, b) => a.rank - b.rank)
                .slice(0, CONFIG.batch_size);

            if (!todo.length) {
                break;
            }
            if (!(await resolve_batch(todo))) {
                break;
            }
        }
    } finally {
        resolving = false;
    }
}

function message_of(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}

async function reload(): Promise<void> {
    current_sort = select("sort").value;
    el("status").hidden = false;
    el("status").textContent = "Loading the catalogue…";
    $cards.hidden = true;
    $table_container.hidden = true;

    try {
        await load_catalogue(current_sort);
    } catch (err) {
        el("status").textContent = `Could not load the catalogue: ${message_of(err)}`;
        return;
    }

    // the bench rows are one small request, so they land long before the sweep
    try {
        await load_bench();
    } catch (err) {
        el("status").hidden = false;
        el("status").textContent = `Catalogue loaded, but the benchmark results did not: ${message_of(err)}`;
    }

    render();
    void resolve_all();
}

/* ------------------------------------------------------------------ *
 * Editing
 * ------------------------------------------------------------------ */

function run_tables(target: Target | null): Node[] {
    const languages = [
        ...new Set([
            ...CONFIG.languages,
            ...CONFIG.run_kinds.flatMap((kind) => Object.keys(target?.runs?.[kind] || {})),
        ]),
    ];

    return CONFIG.run_kinds.map((kind) => {
        const table = clone("run-table-template");
        part(table, ".run-kind").textContent = `${kind} run`;

        const rows = languages.map((language) => {
            const run = target?.runs?.[kind]?.[language];
            const row = clone<HTMLTableRowElement>("run-row-template");
            row.dataset.run = kind;
            row.dataset.language = language;
            part(row, ".run-language").textContent = language;

            for (const field of row.querySelectorAll<HTMLInputElement>("input[data-field]")) {
                const value = run?.[field.dataset.field as keyof Measurement];
                field.value = value === null || value === undefined ? "" : String(value);
            }

            return row;
        });

        replace_children(part(table, "tbody"), rows);
        return table;
    });
}

/**
 * Split a pull reference the way `ollama pull` reads it.
 *
 * The model field asks for what you would paste into a pull command, and that
 * routinely carries a tag, so storing "unsloth/Qwen4-GGUF:Q4_K_M" as a model
 * name with a colon in it would be the wrong reading of what was typed.
 */
function split_reference(reference: string): { model: string; tag: string } {
    const at = reference.indexOf(":");
    return at < 0 ? { model: reference, tag: "" } : { model: reference.slice(0, at), tag: reference.slice(at + 1) };
}

/**
 * Fill and open the editor.
 *
 * An empty `name` puts it in naming mode, for a model that has no card to be
 * opened from. See open_new_editor.
 */
function show_editor(name: string, target: Target | null, tags: string[], tag: string): void {
    const naming = !name;
    editing = { name, target };

    el("edit-model-field").hidden = !naming;
    el("editor-title").textContent = name || "Record a model";
    el("editor-hint").textContent = naming
        ? "For a model the catalogue cannot see: a namespaced build, or a GGUF you" +
          " pulled yourself. Give the name you would pass to `ollama pull`."
        : target
          ? `Stored as ${target.key}, last touched ${target.updated_at || "never"}.` +
            " A different tag is recorded as a row of its own."
          : "Nothing recorded for this model yet.";

    input("edit-model").value = "";
    input("edit-tag").value = tag;
    // a datalist option carries only a value; a label would show up beside it
    replace_children(
        el("edit-tags"),
        tags.map((value) => option(value, "", false)),
    );
    replace_children(
        select("edit-status"),
        CONFIG.statuses.map((value) => option(value, value, value === (target?.status || "benched"))),
    );
    // a row that does not exist yet has its source worked out from the name by
    // the server, which owns the rules; picking one by hand is only a chance to
    // disagree with them
    replace_children(select("edit-source"), [
        ...(target ? [] : [option("", "from the name", true)]),
        ...CONFIG.sources.map((value) => option(value, value, value === target?.source)),
    ]);

    input("edit-memory").value = target?.memory_bytes ? (target.memory_bytes / 1e9).toFixed(1) : "";
    const gpu_fraction = target?.gpu_fraction;
    input("edit-gpu").value =
        gpu_fraction === null || gpu_fraction === undefined ? "" : String(Math.round(gpu_fraction * 100));
    el<HTMLTextAreaElement>("edit-notes").value = target?.notes || "";

    replace_children(el("editor-runs"), run_tables(target));
    el("editor-delete").hidden = !target;
    el("editor-error").hidden = true;

    $editor.showModal();
}

function open_editor(name: string, tag: string): void {
    const entry = catalogue.get(name);
    if (!entry) {
        return;
    }

    const fits = fitting_variants(entry) || [];
    const target = bench_by_key.get(bench_key(name, tag)) || best_target(entry);

    // an off-catalogue model publishes no tags, so offer the ones it was recorded under
    const tags = entry.off_catalogue
        ? bench_targets(entry).map((row) => row.tag)
        : (entry.variants || []).map((variant) => variant.tag);

    show_editor(name, target, tags, target?.tag || tag || fits[0]?.tag || "latest");
}

/**
 * Record a model the catalogue cannot see.
 *
 * A namespaced or hand-built GGUF model only gets a card once it has a bench
 * row (add_off_catalogue), and the edit buttons live on cards, so without a way
 * in from outside there is no way to write that first row at all.
 */
function open_new_editor(): void {
    show_editor("", null, [], "latest");
}

function editor_failed(message: string): void {
    el("editor-error").textContent = message;
    el("editor-error").hidden = false;
}

async function send(url: string, method: string, body?: unknown): Promise<Target> {
    const res = await fetch(url, {
        method,
        headers: body ? { "content-type": "application/json" } : {},
        body: body ? JSON.stringify(body) : undefined,
    });

    if (res.status === 401) {
        // the session expired while the page stayed open
        window.location.href = `/login?next=${encodeURIComponent("/ollama/")}`;
        throw new Error("Logged out.");
    }
    if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))) as { detail?: string };
        throw new Error(detail.detail || `HTTP ${res.status}`);
    }

    return res.json() as Promise<Target>;
}

function number_or_null(value: string): number | null {
    const text = String(value ?? "").trim();
    return text === "" ? null : Number(text);
}

function measurement_url(target: Target, row: HTMLElement): string {
    return (
        `${CONFIG.bench_url}/${target.id}/measurement` +
        `?run_kind=${encodeURIComponent(row.dataset.run || "")}` +
        `&language=${encodeURIComponent(row.dataset.language || "")}`
    );
}

/** Push one language of one run, returning the target as the server now holds it. */
async function save_run(target: Target, row: HTMLElement): Promise<Target> {
    const values: Measurement = { correctness: null, duration_s: null, samples: null };
    for (const field of row.querySelectorAll<HTMLInputElement>("input[data-field]")) {
        values[field.dataset.field as keyof Measurement] = number_or_null(field.value);
    }

    const stored = target.runs?.[row.dataset.run || ""]?.[row.dataset.language || ""];
    const empty = Object.values(values).every((value) => value === null);

    if (empty && stored) {
        return send(measurement_url(target, row), "DELETE");
    }
    if (empty) {
        return target; // nothing stored, nothing entered
    }

    const unchanged =
        stored &&
        stored.correctness === values.correctness &&
        stored.duration_s === values.duration_s &&
        stored.samples === values.samples;
    if (unchanged) {
        return target;
    }

    return send(`${CONFIG.bench_url}/${target.id}/measurement`, "PUT", {
        run_kind: row.dataset.run,
        language: row.dataset.language,
        ...values,
    });
}

async function save_editor(): Promise<void> {
    if (!editing) {
        return;
    }

    const reference = split_reference((editing.name || input("edit-model").value).trim());
    if (!reference.model) {
        editor_failed("A model needs a name.");
        return;
    }

    const memory_gb = number_or_null(input("edit-memory").value);
    const gpu_percent = number_or_null(input("edit-gpu").value);
    const source = select("edit-source").value;

    const payload = {
        model: reference.model,
        // a tag typed into the model field is the more explicit of the two
        tag: reference.tag || input("edit-tag").value.trim() || "latest",
        status: select("edit-status").value,
        // empty means "work it out from the name", which the server does for a
        // row it is creating; sending "" would fail its vocabulary check
        ...(source ? { source } : {}),
        memory_bytes: memory_gb === null ? null : Math.round(memory_gb * 1e9),
        gpu_fraction: gpu_percent === null ? null : gpu_percent / 100,
        notes: el<HTMLTextAreaElement>("edit-notes").value.trim(),
    };

    try {
        let target = await send(CONFIG.bench_url, "POST", payload);

        for (const row of el("editor-runs").querySelectorAll<HTMLElement>("tr[data-run]")) {
            target = await save_run(target, row);
        }

        // a tag change moves the row to a different key, so rebuild from the server
        await load_bench();
        render();
        $editor.close();
    } catch (err) {
        editor_failed(message_of(err));
    }
}

async function delete_editor(): Promise<void> {
    if (!editing?.target) {
        return;
    }

    try {
        await send(`${CONFIG.bench_url}/${editing.target.id}`, "DELETE");
        await load_bench();
        render();
        $editor.close();
    } catch (err) {
        editor_failed(message_of(err));
    }
}

/* ------------------------------------------------------------------ *
 * Wiring
 * ------------------------------------------------------------------ */

/**
 * Move a card's selection to one quant.
 *
 * render() rebuilds every card, so the chip that had focus is gone by the time
 * this returns; keyboard navigation would drop the reader back to the top of the
 * page without putting it back.
 */
function pick_tag(model: string, tag: string, refocus: boolean): void {
    chosen_tags.set(model, tag);
    render();

    if (!refocus) {
        return;
    }

    document.querySelector<HTMLElement>(`[data-pick="${CSS.escape(model)}"][data-tag="${CSS.escape(tag)}"]`)?.focus();
}

function toggle_hidden(name: string): void {
    if (!hidden_models.delete(name)) {
        hidden_models.add(name);
    }

    save_hidden();
    render();
}

/** Arrow keys inside one chip strip; a radiogroup is expected to wrap around. */
const CHIP_STEPS: Record<string, number> = {
    ArrowRight: 1,
    ArrowDown: 1,
    ArrowLeft: -1,
    ArrowUp: -1,
};

function on_chip_key(chip: HTMLElement, event: KeyboardEvent): void {
    const group = chip.closest<HTMLElement>('[role="radiogroup"]');
    if (!group) {
        return;
    }

    const chips = [...group.querySelectorAll<HTMLElement>("[data-pick]")];
    const at = chips.indexOf(chip);
    let next: number;

    if (event.key in CHIP_STEPS) {
        next = (at + CHIP_STEPS[event.key] + chips.length) % chips.length;
    } else if (event.key === "Home") {
        next = 0;
    } else if (event.key === "End") {
        next = chips.length - 1;
    } else {
        return;
    }

    event.preventDefault();
    pick_tag(chip.dataset.pick || "", chips[next]?.dataset.tag || "", true);
}

function on_header_click(header: HTMLTableCellElement): void {
    const key = header.dataset.sort || "rank";
    // same column toggles direction; a new column starts descending for sizes,
    // counts and scores, ascending for the catalogue's own ordering
    if (key === sort_key) {
        sort_desc = !sort_desc;
    } else {
        apply_order(key);
    }

    // keep the select honest about what the table is doing
    const order = select("order");
    order.value = [...order.options].some((candidate) => candidate.value === key) ? key : "";
    save_view_state();
    render();
}

function init(): void {
    load_hidden();
    load_view_state();
    render_session();

    // a new budget drops every hand-picked quant: a tag chosen at 24GB means
    // nothing at 12, so the cards go back to proposing one themselves
    for (const id of ["vram", "headroom"]) {
        el(id).addEventListener("input", () => {
            chosen_tags.clear();
            save_view_state();
            render();
        });
    }

    for (const id of ["search", "capability", "only-fitting", "coverage"]) {
        el(id).addEventListener("input", () => {
            save_view_state();
            render();
        });
    }

    select("order").addEventListener("change", () => {
        apply_order(select("order").value);
        save_view_state();
        render();
    });
    select("sort").addEventListener("change", () => {
        save_view_state();
        void reload();
    });

    el("hidden-toggle").addEventListener("click", () => {
        show_hidden = !show_hidden;
        save_view_state();
        render();
    });

    $cards_view.addEventListener("click", () => set_view("cards"));
    $table_view.addEventListener("click", () => set_view("table"));

    el("pause").addEventListener("click", () => {
        paused = !paused;
        el("pause").textContent = paused ? "Resume" : "Pause";
        if (!paused) {
            void resolve_all();
        }
    });

    // one listener for both views, since every card and row is rebuilt constantly
    document.addEventListener("click", (event) => {
        const element = event.target as Element | null;

        const edit = element?.closest<HTMLElement>("[data-edit]");
        if (edit) {
            open_editor(edit.dataset.edit || "", edit.dataset.tag || "");
            return;
        }

        const hide = element?.closest<HTMLElement>("[data-hide]");
        if (hide) {
            toggle_hidden(hide.dataset.hide || "");
            return;
        }

        const pick = element?.closest<HTMLElement>("[data-pick]");
        if (pick) {
            pick_tag(pick.dataset.pick || "", pick.dataset.tag || "", true);
        }
    });

    document.addEventListener("keydown", (event) => {
        const chip = (event.target as Element | null)?.closest<HTMLElement>("[data-pick]");
        if (chip) {
            on_chip_key(chip, event);
        }
    });

    el("record-model").addEventListener("click", () => open_new_editor());
    el("editor-save").addEventListener("click", () => void save_editor());
    el("editor-delete").addEventListener("click", () => void delete_editor());
    el("editor-cancel").addEventListener("click", () => $editor.close());

    for (const header of document.querySelectorAll<HTMLTableCellElement>("th[data-sort]")) {
        header.addEventListener("click", () => on_header_click(header));
    }

    void reload();
}

init();

export {};
